import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't auto-redirect to login for auth endpoints (magic-link, verify-email, etc.)
    const authEndpoints = [
      '/auth/verify-magic-link',
      '/auth/verify-email',
      '/auth/register',
      '/auth/magic-link',
      '/auth/resend-verification'
    ];
    
    const isAuthEndpoint = authEndpoints.some(endpoint => 
      error.config?.url?.includes(endpoint)
    );
    
    // Handle 429 (Rate Limit) errors
    if (error.response?.status === 429) {
      const retryAfter = error.response?.data?.retryAfter || 60;
      console.warn(`Rate limit exceeded for ${error.config?.url}. Retry after ${retryAfter} seconds.`);
      // Don't throw for 429 on /auth/me - let the calling code handle it
      if (error.config?.url?.includes('/auth/me')) {
        return Promise.reject(error);
      }
    }
    
    if (error.response?.status === 401 && !isAuthEndpoint) {
      // Token expired or invalid - only redirect for protected routes
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      const prefix = window.location.pathname.startsWith('/en') ? '/en' : '';
      window.location.href = `${prefix}/login`;
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth API
export const authAPI = {
  getFormToken: () => api.get('/auth/form-token'),
  register: (name, email, password, termsAccepted, locale, formToken, website = '') =>
    api.post('/auth/register', {
      name,
      email,
      password,
      termsAccepted: termsAccepted.toString(),
      locale: locale || 'fr',
      formToken,
      website,
    }),
  verifyEmail: (token) => api.post(`/auth/verify-email/${token}`),
  sendMagicLink: (email) => api.post('/auth/magic-link', { email }),
  verifyMagicLink: (token) => api.post(`/auth/verify-magic-link/${token}`),
  resendVerificationEmail: (email, formToken, website = '') =>
    api.post('/auth/resend-verification', { email, formToken, website }),
  getMe: () => api.get('/auth/me'),
  acceptTerms: () => api.post('/auth/accept-terms')
};

// User API
export const userAPI = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data) => api.put('/users/profile', data),
  deleteAccount: () => api.delete('/users/account')
};

// Galene API
export const galeneAPI = {
  createLink: (data) => api.post('/galene/create-link', data),
  getLinks: () => api.get('/galene/links'),
  getLink: (id) => api.get(`/galene/links/${id}`),
  deleteLink: (id) => api.delete(`/galene/links/${id}`),
  getStats: () => api.get('/galene/stats'),
  getLinkTokens: (id) => api.get(`/galene/links/${id}/tokens`),
  getLinkProperties: (id) => api.get(`/galene/links/${id}/properties`),
  sendLinkEmail: (sessionId, data) => api.post(`/galene/send-link-email/${sessionId}`, data),
  getActiveSessions: () => api.get('/galene/active-sessions'),
  completeSession: (sessionId) => api.post(`/galene/sessions/${sessionId}/complete`)
};

// Payment API
export const paymentAPI = {
  createCheckout: (packType, promoCode) => api.post('/payments/create-checkout', { packType, promoCode }),
  validatePromo: (promoCode) => api.post('/payments/validate-promo', { promoCode }),
  getHistory: () => api.get('/payments/history'),
  getPacks: () => api.get('/payments/packs'),
  getBillingPortal: () => api.get('/payments/billing-portal')
};

// Session API
export const sessionAPI = {
  getSessions: (params) => api.get('/sessions', { params }),
  getSession: (id) => api.get(`/sessions/${id}`),
  updateSession: (id, data) => api.put(`/sessions/${id}`, data),
  getStats: () => api.get('/sessions/stats')
};

// Statistics API
export const statisticsAPI = {
  getOverview: () => api.get('/statistics/overview'),
  getCharts: (period) => api.get('/statistics/charts', { params: { period } }),
  getLinksSummary: () => api.get('/statistics/links-summary')
};



